import * as React from 'react';
import {
    Button,
    FormGroup,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { ApiRoutes } from '../Common';
import { FlexStartRow, FormLabel, toasty } from '../common/forms/FormElements';
import { Contract } from '../contracts/Contract';
import {
    RejectionNote,
    TimesheetAuditType,
} from '../timesheet/Timesheet';
import { util } from '../Util';
import { DateTimeInputFormat } from '../dispatch/Dispatch';

export class Job {
    contractId = 0;

    contractSequence = 0;

    weekOfDate = '';

    requestedById = 0;

    foremanId = 0;

    startTime = '';

    dispatchOfficeLocationId = '';

    dispatchOfficeLocationName = '';

    workOrderNumber = '';

    jobCity = '';

    notes = '';

    resources = [];

    nonResources = [];

    overrides = [];

    contract = new Contract();

    meetingLocation = null; // Address()

    jobLocation = null; // Address()

    hasAssignments = false;

    tenantSettings = null;

    materials = [];
}

export class JobDetails extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            saving: false,
            workOrderNumberValid: true,
            foremanValid: true,
            showEmergencyConfirmModal: false,
            isCallingOutAssignment: false,
            isCancellingJob: false,
            showCancelJobModal: false,
        };
    }

    onClientCancel = async () => {
        const {
            timesheetId,
            timesheetNumber,
            onRefresh,
            jobId,
            jobStartTimeRaw,
        } = { ...this.props };

        const { selectedJobCancellationTime, selectedJobCancellationNotes } = {
            ...this.state,
        };

        const jobDate = new Date(jobStartTimeRaw);
        const JobDay = jobDate.getDay();

        const model = {
            jobId,
            day: JobDay,
            cancellationTypeId: 1, // client cancel only
            cancellationNote: selectedJobCancellationNotes,
            cancellationTime: selectedJobCancellationTime,
            sendNotifications: false,
        };

        this.setState({ isCancellingJob: true });

        const response = await util.fetch.post(
            ApiRoutes.dispatch.cancelJob(),
            model,
        );

        if (response === 'ok') {
            await util.fetch.put(ApiRoutes.timesheet.audit(), {
                timesheetId,
                timesheetNumber,
                timesheetAuditTypeId: TimesheetAuditType.ClientCancelApplied,
                systemMessage: selectedJobCancellationTime,
                userNotes: selectedJobCancellationNotes,
            });

            this.setState({
                showCancelJobModal: false,
                isCancellingJob: false,
                selectedJobCancellationTime: DateTimeInputFormat(new Date()),
                selectedJobCancellationNotes: '',
            });

            toasty.success('Client Cancellation Successful');
            onRefresh();
        } else {
            this.setState({
                showCancelJobModal: false,
                isCancellingJob: false,
                selectedJobCancellationTime: DateTimeInputFormat(new Date()),
                selectedJobCancellationNotes: '',
            });

            toasty.error('Client Cancellation Error');
        }
    };

    onClientCancelClick = () => {
        this.setState({
            selectedJobCancellationTime: DateTimeInputFormat(new Date()),
            showCancelJobModal: true,
        });
    };

    onEmergencyAssignment = async () => {
        this.setState({ isCallingOutAssignment: true });
        const {
            onRefresh,
            timesheetId,
            isEmergency,
            isBillingEmergency,
            isPayrollEmergency,
            isPayroll,
            isBilling,
            isTimesheet,
        } = this.props;

        let isEmergencyOverall = false;
        if (isTimesheet && isEmergency) isEmergencyOverall = true;
        if (isBilling && isBillingEmergency) isEmergencyOverall = true;
        if (isPayroll && isPayrollEmergency) isEmergencyOverall = true;

        const model = {
            timesheetId,
            isEmergency: !isEmergencyOverall,
        };
        let route = ApiRoutes.timesheet.setEmergency();
        if (isPayroll) route = ApiRoutes.payrollManagement.setEmergency();
        else if (isBilling) route = ApiRoutes.billing.setEmergency();

        const response = await util.fetch.post(route, model);

        if (response.success) {
            onRefresh(); // refreshes reused
        } else {
            toasty.warning('Error adjusting emergency status.', {
                autoClose: false,
                closeOnClick: true,
            });
        }

        this.setState({
            showEmergencyConfirmModal: false,
            isCallingOutAssignment: false,
            selectedEmergencyAssignments: null,
        });
    };

    onEmergencyClicked = () => {
    // validation?
        const { isSelfDispatching } = { ...this.props };

        if (isSelfDispatching) return false;

        // open a modal for "are you sure"
        this.toggleEmergencyConfirmModal();
    };

    onUndoClientCancel = async () => {
        await this.setState({ undoingClientCancel: true });

        const { timesheetId, timesheetNumber, onRefresh } = { ...this.props };

        let success = false;
        try {
            const response = await util.fetch.put(
                ApiRoutes.timesheet.undoClientCancel(),
                timesheetId,
                util.fetch.format.none,
            );

            success = !!response.ok;
        } catch {
            success = false;
        } finally {
            this.setState({ undoingClientCancel: false });
        }

        if (success) {
            await util.fetch.put(ApiRoutes.timesheet.audit(), {
                timesheetId,
                timesheetNumber,
                timesheetAuditTypeId: TimesheetAuditType.ClientCancelRemoved,
                systemMessage: '',
                userNotes: '',
            });

            toasty.success('Client Cancellation Undone.');
            onRefresh();
        } else {
            toasty.error(
                'There was an error undoing the client cancel. Please try your request again.',
            );
        }
    };

    onUndoClientCancelClick = async () => {
        this.setState({
            showUndoClientCancelModal: true,
        });
    };

    loadOptions = (inputValue, callback) => {
        fetch(ApiRoutes.typeAheads.jobCities(inputValue))
            .then((response) => response.json())
            .then((data) => callback(data));
    };

    saveTimesheetWorkOrderNumber = async (evt) => {
        await this.setState({ workOrderNumberValid: true });
        const {
            onRefresh,
            requireWorkOrder,
            workOrderNumber,
            timesheetId,
            timesheetNumber,
            foremanId,
        } = { ...this.props };

        // Validation
        if (!!requireWorkOrder && !workOrderNumber) {
            this.setState({ workOrderNumberValid: false });
            return false;
        }

        if (!foremanId) {
            this.setState({ foremanValid: false });
            return false;
        }

        const { saving } = { ...this.state };
        if (!saving) {
            await this.setState({ saving: true });

            let success = false;
            try {
                const response = await util.fetch.put(
                    ApiRoutes.timesheet.updateWorkOrderNumber(timesheetId),
                    workOrderNumber,
                    util.fetch.format.none,
                );
                success = !!response.ok;
            } catch {
                success = false;
            } finally {
                this.setState({ saving: false });
            }

            if (success) {
                toasty.success(
                    `[${timesheetNumber}]: Work Order Number Updated.`,
                );
                onRefresh();
            } else {
                toasty.error(
                    'There was an error updating the work order number. Please try your request again.',
                );
            }
        }
    };

    toggleCancelJobModal = () => {
        const { showCancelJobModal } = this.state;
        this.setState({ showCancelJobModal: !showCancelJobModal });
    };

    toggleEmergencyConfirmModal = () => {
        const { showEmergencyConfirmModal, isCallingOutAssignment } = this.state;

        if (showEmergencyConfirmModal == false) this.isCallingOutAssignment = false;

        this.setState({
            showEmergencyConfirmModal: !showEmergencyConfirmModal,
            isCallingOutAssignment,
        });
    };

    toggleUndoConfirmModal = () => {
        const { showUndoClientCancelModal } = this.state;
        this.setState({
            showUndoClientCancelModal: !showUndoClientCancelModal,
        });
    };

    render() {

        const {
            jobStartTime,
            lastRejectionNote,
            timesheetNumber,
            foremanName,
            foremanPhone,
            timesheetStatus,
            statusRejectionNote,
            isClientCancelled,
            hours,
            tonnage,
            loads,

            isEmergency,
            isTimesheet,
            isPayroll,
            isBilling,
            isBillingEmergency,
            isPayrollEmergency,
            isSelfDispatching,
            isNonFlagging,
        } = { ...this.props };

        const {
            isCallingOutAssignment,
            showEmergencyConfirmModal,
            showCancelJobModal,
            showUndoClientCancelModal,
            selectedJobCancellationTime,
            selectedJobCancellationNotes,
            isCancellingJob,
            undoingClientCancel,
        } = this.state;

        let isEmergencyOverall = false;
        if (isTimesheet && isEmergency) isEmergencyOverall = true;
        if (isBilling && isBillingEmergency) isEmergencyOverall = true;
        if (isPayroll && isPayrollEmergency) isEmergencyOverall = true;

        const foremanNameFormatted = (foremanName ?? '-') + (foremanPhone ? ` - ${foremanPhone}` : '');

        return (
            <div className="job-confirm-details">
                {!!timesheetNumber && (
                    <FlexStartRow>
                        <FormLabel
                            htmlFor="timesheetNumber"
                            text="Driver"
                        />
                        <span
                            id="timesheetNumber"
                            name="timesheetNumber"
                            className="text-muted"
                            style={{ marginLeft: '16px' }}
                        >
                            
                        </span>
                        {!!isSelfDispatching && (
                            <span
                                className="ml-2 badge badge-info"
                                title={
                                    isNonFlagging
                                        ? 'This is a non-flagging timesheet'
                                        : 'This is a self-dispatching timesheet'
                                }
                                style={{
                                    fontSize: '1em',
                                    marginTop: '-2px',
                                    padding: '2px 4px',
                                }}
                            >
                                {isNonFlagging ? 'Daily Driving Log' : 'SD'}
                            </span>
                        )}
                    </FlexStartRow>
                )}
                {!!timesheetStatus && (
                    <FlexStartRow className="align-items-start">
                        <FormLabel
                            htmlFor="timesheetStatus"
                            text="Status"
                        />
                        {timesheetStatus !== 'Rejected' && (
                            <span
                                className={`timesheet-status-label form-control-sm  ${(
                                    timesheetStatus ?? ''
                                )
                                    .replace(' ', '')
                                    .toLowerCase()}`}
                            >
                                {timesheetStatus}
                            </span>
                        )}
                        {timesheetStatus === 'Rejected'
                                && !!lastRejectionNote && (
                            <div style={{ marginLeft: '20px' }}>
                                {!!lastRejectionNote && (
                                    <RejectionNote
                                        note={lastRejectionNote}
                                    />
                                )}
                            </div>
                        )}
                        {timesheetStatus === 'Billing-Payroll Rejected'
                                && !!statusRejectionNote && (
                            <div style={{ marginLeft: '20px' }}>
                                {!!statusRejectionNote && (
                                    <RejectionNote
                                        note={statusRejectionNote}
                                    />
                                )}
                            </div>
                        )}
                        {timesheetStatus === 'Rejected'
                                && !lastRejectionNote && (
                            <div
                                style={{ marginLeft: '20px' }}
                                className="site-alert alert-danger text-center"
                            >
                                  REJECTED
                            </div>
                        )}

                        {isClientCancelled && (
                            <>
                                <span className="timesheet-status-label">
                                    {' '}
              -
                                    {' '}
                                </span>
                                <span
                                    className="timesheet-status-label form-control-sm client-cancel"
                                >
              Client Cancel
                                </span>
                            </>
                        )}
                    </FlexStartRow>
                )}

                <FlexStartRow>
                    <FormLabel htmlFor="jobStartTime" text="Date" />
                    <span
                        id="jobStartTime"
                        name="jobStartTime"
                        className="form-control-sm form-control-plaintext"
                    >
                        {jobStartTime ?? ''}
                    </span>
                </FlexStartRow>

                <FlexStartRow>
                    <FormLabel htmlFor="jobStartTime" text="Hours" />
                    <span
                        id="jobStartTime"
                        name="jobStartTime"
                        className="form-control-sm form-control-plaintext"
                    >
                        {hours}
                    </span>
                </FlexStartRow>

                <FlexStartRow>
                    <FormLabel htmlFor="jobStartTime" text="Tonnage" />
                    <span
                        id="jobStartTime"
                        name="jobStartTime"
                        className="form-control-sm form-control-plaintext"
                    >
                        {tonnage}
                    </span>
                </FlexStartRow>

                <FlexStartRow>
                    <FormLabel htmlFor="jobStartTime" text="Loads" />
                    <span
                        id="jobStartTime"
                        name="jobStartTime"
                        className="form-control-sm form-control-plaintext"
                    >
                        {loads}
                    </span>
                </FlexStartRow>

                <FlexStartRow>
                    <FormLabel
                        htmlFor="foremanName"
                        text="Foreman"
                    />
                    <a
                        id="foremanName"
                        name="foremanName"
                        readOnly
                        className="form-control-sm form-control-plaintext"
                        href={`tel:${foremanPhone.replace(/-/g, '')}`}>
                        {foremanNameFormatted}
                    </a>
                </FlexStartRow>

                <Modal
                    isOpen={showEmergencyConfirmModal}
                    toggle={this.toggleEmergencyConfirmModal}
                >
                    <ModalHeader toggle={this.toggleEmergencyConfirmModal}>
            Emergency confirmation
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <p>
                                {!!isEmergencyOverall === false
                                    ? 'Are you sure you would like to set this job as emergency work?'
                                    : 'Are you sure you would like to remove emergency work from this job?'}
                            </p>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        {isCallingOutAssignment && (
                            <FontAwesomeIcon
                                icon={faCircleNotch}
                                className="fa-spin mr-2"
                                size="sm"
                            />
                        )}
                        <Button
                            color="primary"
                            disabled={isCallingOutAssignment}
                            onClick={this.onEmergencyAssignment}
                        >
              Ok
                        </Button>
                        {' '}
                    </ModalFooter>
                </Modal>

                <Modal
                    backdrop="static"
                    keyboard={false}
                    isOpen={showCancelJobModal}
                    toggle={this.toggleCancelJobModal}
                >
                    <ModalHeader toggle={this.toggleCancelJobModal}>
            Client Cancellation
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <label>{`Job Start Time: ${jobStartTime}`}</label>
                        </FormGroup>
                        <FormGroup>
                            <label>Cancellation Time</label>
                            <input
                                id="selectedJobCancellationTime"
                                required
                                type="datetime-local"
                                className="form-control"
                                name="selectedJobCancellationTime"
                                defaultValue={
                                    selectedJobCancellationTime ?? ''
                                }
                                onChange={(ev) => {
                                    const val = ev.target.value;
                                    this.setState({
                                        selectedJobCancellationTime: val,
                                    });
                                }}
                            />
                            <small className="invalid-feedback text-danger">
                Cancellation Time is required.
                            </small>
                        </FormGroup>
                        <FormGroup>
                            <label>Notes</label>
                            <textarea
                                id="notes"
                                name="selectedJobCancellationNotes"
                                className="form-control"
                                defaultValue={
                                    selectedJobCancellationNotes ?? ''
                                }
                                placeholder="Enter optional notes regarding the cancellation."
                                type="text"
                                maxLength="500"
                                rows="5"
                                onChange={(ev) => {
                                    const val = ev.target.value;
                                    this.setState({
                                        selectedJobCancellationNotes: val,
                                    });
                                }}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        {isCancellingJob && (
                            <FontAwesomeIcon
                                icon={faCircleNotch}
                                className="fa-spin mr-2"
                                size="sm"
                            />
                        )}
                        <Button
                            color="primary"
                            onClick={this.onClientCancel}
                        >
              Ok
                        </Button>
                        {' '}
                    </ModalFooter>
                </Modal>

                <Modal
                    isOpen={showUndoClientCancelModal}
                    toggle={() => this.toggleUndoConfirmModal()}
                >
                    <ModalHeader
                        toggle={() => this.toggleUndoConfirmModal()}
                    >
            Undo Client Cancel
                    </ModalHeader>
                    <ModalBody>
                        <p>
              Are you sure you would like to undo the client
              cancel?
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        {undoingClientCancel && (
                            <FontAwesomeIcon
                                icon={faCircleNotch}
                                className="fa-spin mr-2"
                                size="sm"
                            />
                        )}
                        <Button
                            color="primary"
                            disabled={undoingClientCancel}
                            onClick={() => this.onUndoClientCancel()}
                        >
              Ok
                        </Button>
                        {' '}
                        <Button
                            color="secondary"
                            onClick={() => this.setState({
                                showUndoClientCancelModal: false,
                            })}
                        >
              Cancel
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>
        );
    }
}
